/**
 * API Configuration and Utilities
 * Centralized API management for the AFF Portal
 */

import { API_CONFIG, API_ENDPOINTS } from './constants';

/**
 * Build complete API URL
 * @param {string} endpoint - API endpoint
 * @returns {string} Complete API URL
 */
export const buildApiUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

/**
 * Get authentication headers
 * @returns {Object} Headers with authorization token
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/**
 * Check if backend is available
 * @returns {Promise<boolean>} Backend availability status
 */
export const isBackendAvailable = async () => {
  try {
    const response = await fetch(buildApiUrl(process.env.REACT_APP_HEALTH_CHECK), {
      method: 'HEAD',
      timeout: 5000
    });
    return response.ok;
  } catch (error) {
    return false;
  }
};

/**
 * API endpoint getters
 */
export const getAuthUrl = (endpointKey, ...params) => {
  const endpoint = API_ENDPOINTS.AUTH[endpointKey];
  return typeof endpoint === 'function' ? buildApiUrl(endpoint(...params)) : buildApiUrl(endpoint);
};
export const getMembersUrl = (endpointKey, ...params) => {
  const endpoint = API_ENDPOINTS.MEMBERS[endpointKey];
  return typeof endpoint === 'function' ? buildApiUrl(endpoint(...params)) : buildApiUrl(endpoint);
};


export const getApiUrl = (endpointKey, ...params) => {
  // Check if it's a member endpoint (includes Xero endpoints)
  if (API_ENDPOINTS.MEMBERS && API_ENDPOINTS.MEMBERS[endpointKey]) {
    return getMembersUrl(endpointKey, ...params);
  }
  // Check if it's an auth endpoint
  if (API_ENDPOINTS.AUTH && API_ENDPOINTS.AUTH[endpointKey]) {
    return getAuthUrl(endpointKey, ...params);
  }
  // Check if it's a Google Sheets endpoint
  if (API_ENDPOINTS.GOOGLE_SHEETS && API_ENDPOINTS.GOOGLE_SHEETS[endpointKey]) {
    const endpoint = API_ENDPOINTS.GOOGLE_SHEETS[endpointKey];
    return typeof endpoint === 'function' ? buildApiUrl(endpoint(...params)) : buildApiUrl(endpoint);
  }
  // Default to members for now
  return getMembersUrl(endpointKey, ...params);
};

/**
 * Default fetch options
 */
export const getDefaultFetchOptions = () => ({
  headers: getAuthHeaders(),
  timeout: API_CONFIG.TIMEOUT,
});

/**
 * API Configuration object
 */
export const apiConfig = {
  baseUrl: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  retryAttempts: API_CONFIG.RETRY_ATTEMPTS,
  retryDelay: API_CONFIG.RETRY_DELAY,
  endpoints: API_ENDPOINTS,
  isBackendAvailable,
  buildApiUrl,
  getAuthHeaders,
  getDefaultFetchOptions,
};
